import { Box, Grid, Paper, Typography } from "@mui/material";
import { SortDirection } from "ka-table";
import { ChangeEvent, useMemo, useState } from "react";
import AddCandidateModal from "../App/Components/AddCandidate/AddCandidate";
import DynamicSearchComponent from "../App/Components/DynamicSearchComponent/DynamicSearchComponent";
import CSVImportModal from "../App/Components/ImportCSV/CSVImportModal";
import { DisplayImportStatus } from "../App/Components/ImportCSV/DisplayImportStatus";
import { PageContainer } from "../App/Components/PageContainer/PageContainer";
import { PageTitle } from "../App/Components/PageTitle/PageTitle";
import { buildLocalStorageKey, readLocalStorageColumns } from "../App/Components/Table/CrmTable";
import { CustomFieldTypes } from "../App/Enums/CustomFieldTypes";
import { useFormDataForClientQuery } from "../App/Queries/CampaignQueries";
import { useCandidateSearchQuery } from "../App/Queries/CandidateQueries";
import { useSelectedClientCustomFields } from "../App/Queries/ClientQueries";
import { useSearchTagsQuery } from "../App/Queries/FilterQueries";
import { useGroupsQuery } from "../App/Queries/GroupQueries";
import { useAppSelector } from "../App/State/Hooks/reduxHooks";
import { useDebouncedEffect } from "../App/State/Hooks/useDebouncedEffect";
import { selectClientValue } from "../App/State/Slices/SelectedClientSlice";
import { BaseFormField, IPaginationRequest } from "../App/Typings/Campaigns";
import { IClientCustomField } from "../App/Typings/Client";
import { Rule } from "../App/Typings/DynamicGroupRules";
import {
  clearOldLocalStorage,
  getSessionStorage,
  setSessionStorage,
  STORAGE_VERSION_DATE,
} from "../App/Utilities/browserStorage";
import { FieldType } from "../DynamicGroup/DynamicGroup";
import MessageDialogCandidateEmail from "../MessageComponents/MessageDialog/MessageDialogCandidateEmail";
import MessageDialogCandidateText from "../MessageComponents/MessageDialog/MessageDialogCandidateText";
import { AddToGroupOptions } from "./Components/AddToGroup/AddToGroupOptions";
import { ManageCandidatesButton } from "./Components/ManageCandidatesButton/ManageCandidatesButton";
import { ManageCandidatesTable } from "./Components/ManageCandidatesTable/ManageCandidatesTable";
import { CandidateSelectionProvider } from "./State/ManageCandidateContext";

export interface IGetCandidatesQuery {
  rules: Rule[];
  paginationRequest: IPaginationRequest;
}

const defaultFilterState: IGetCandidatesQuery = {
  paginationRequest: {
    page: 0,
    pageSize: 10,
    search: "",
    sortBy: "createdDate",
    sortDirection: "desc",
  },
  rules: [],
};

function tryGetSavedPaginationRequest(selectedClientId: string): IPaginationRequest {
  const sessionTableState = getSessionStorage<IGetCandidatesQuery>(
    `manage-candidates-${selectedClientId}-${STORAGE_VERSION_DATE}`,
  );
  const getLocalStorageOrDefaultPaginationRequest = () => {
    const localStorageColumns = readLocalStorageColumns(
      buildLocalStorageKey("manage-candidates", selectedClientId),
    );
    const localStorageSortColumn = localStorageColumns.find(
      (col) => col.isSortable && col.sortDirection !== undefined,
    );
    return Object.keys(defaultFilterState.paginationRequest).reduce((acc, key) => {
      if (localStorageSortColumn) {
        if (key === "sortBy") {
          acc[key] = localStorageSortColumn.key;
        } else if (key === "sortDirection") {
          acc[key] = localStorageSortColumn.sortDirection === SortDirection.Ascend ? "asc" : "desc";
        } else {
          acc[key] = defaultFilterState.paginationRequest[key];
        }
      } else {
        acc[key] = defaultFilterState.paginationRequest[key];
      }
      return acc;
    }, {} as IPaginationRequest);
  };

  return sessionTableState?.paginationRequest ?? getLocalStorageOrDefaultPaginationRequest();
}

function Candidates() {
  const selectedClient = useAppSelector(selectClientValue);
  const { data: customFields = [] } = useSelectedClientCustomFields();

  const { data: forms, isLoading: formsLoading } = useFormDataForClientQuery(selectedClient.clientId);
  const { data: tags, isLoading: tagsLoading } = useSearchTagsQuery(selectedClient.clientId);

  const sessionTableState = getSessionStorage<IGetCandidatesQuery>(
    `manage-candidates-${selectedClient.clientId}-${STORAGE_VERSION_DATE}`,
  );
  const storedRules = sessionTableState?.rules ?? defaultFilterState.rules;
  const paginationRequest = useMemo(
    () => tryGetSavedPaginationRequest(selectedClient.clientId),
    [selectedClient.clientId],
  );

  const [search, setSearch] = useState<string>(paginationRequest.search ?? "");
  const [page, setPage] = useState(paginationRequest.page);
  const [pageSize, setPageSize] = useState(paginationRequest.pageSize);
  const [order, setOrder] = useState<"asc" | "desc">(paginationRequest.sortDirection as "asc" | "desc");
  const [orderBy, setOrderBy] = useState<string | undefined>(paginationRequest.sortBy);
  const [rules, setRules] = useState<Rule[]>(storedRules);

  const { data: candidates, isLoading: isCandidatesLoading } = useCandidateSearchQuery(
    JSON.stringify(rules),
    {
      page: page,
      pageSize: pageSize,
      search: search,
      sortBy: orderBy,
      sortDirection: order,
    },
  );

  const { data: groups, isLoading: isGroupsLoading } = useGroupsQuery(false);

  useDebouncedEffect(
    () => {
      setSessionStorage<IGetCandidatesQuery>(
        `manage-candidates-${selectedClient.clientId}-${STORAGE_VERSION_DATE}`,
        {
          paginationRequest: { page, pageSize, search, sortBy: orderBy, sortDirection: order },
          rules,
        },
      );
    },
    [page, pageSize, search, orderBy, order, JSON.stringify(rules)],
    100,
  );

  const getDropDownOptions = (fields: BaseFormField[], key: string): string[] => {
    const f = fields
      .filter((c) => c.key === key)
      .flatMap((c) =>
        c.adGroupOptions && c.adGroupOptions.length > 0
          ? (c?.adGroupOptions ?? []).map((ad) => ad.name).sort()
          : c.dropdownOptions,
      );
    return f;
  };

  const getFieldType = (type: CustomFieldTypes): string => {
    if (type === CustomFieldTypes.ArrayString) {
      return "dropdown";
    }
    if (type === CustomFieldTypes.Upload) {
      return "upload";
    }

    return "text";
  };

  function getCustomFields(fields: IClientCustomField[]) {
    let uniqueFields: FieldType[] = [];

    (fields ?? []).forEach((c) => {
      if (uniqueFields.filter((f) => f.id === `customfield.${c.key}`).length === 0) {
        uniqueFields.push({
          field: {
            fieldName: c.label,
            fieldType: getFieldType(c.fieldType),
            archived: c.isArchived,
            dropdownOptions: getDropDownOptions(
              forms?.flatMap((c) => Object.values(c.formFields)) ?? [],
              c.key,
            ),
          },
          id: `customfield.${c.key}`,
        } as FieldType);
      }
    });

    return uniqueFields;
  }

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSetRules = (newRules: Rule[]) => {
    setRules(newRules);
    setPage(0);
  };

  const handleSetSearch = (newSearch: string) => {
    if (search !== newSearch) {
      setSearch(newSearch);
      setPage(0);
    }
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  return (
    <PageContainer>
      <CandidateSelectionProvider>
        <Paper sx={{ mt: 4, p: { xs: 2, lg: 4 } }}>
          <Grid container>
            <Grid container item xs={12} sx={{ marginBottom: "2em", width: 1 }}>
              <Grid item xs={12} lg={8}>
                <PageTitle sx={{ lineHeight: 1 }}>Manage Candidates</PageTitle>
                <Typography>
                  Total Candidates <strong>{candidates?.totalItems}</strong>
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                lg={4}
                sx={{
                  display: "flex",
                  alignItems: { xs: "center", lg: "flex-end" },
                  justifyContent: { xs: "center", lg: "flex-end" },
                }}
              >
                <DisplayImportStatus />
              </Grid>
            </Grid>
            <Grid item container xs={12} sx={{ marginBottom: "10px" }}>
              <Grid item xs={12} sm={12} md={9}>
                <DynamicSearchComponent
                  props={{
                    rules: rules,
                    formFields: getCustomFields(customFields),
                    onSearchChange: handleSetSearch,
                    onRuleChange: handleSetRules,
                    paginationRequest: {
                      page: page,
                      pageSize: pageSize,
                      search: search,
                      sortBy: orderBy,
                      sortDirection: order,
                    },
                    isCampaign: false,
                    isLoading: isCandidatesLoading || formsLoading || tagsLoading || isGroupsLoading,
                    isFiltersLoading: formsLoading || tagsLoading || isGroupsLoading,
                    tags: tags ?? [],
                    campaigns: forms ?? [],
                    groups: groups?.items ?? [],
                  }}
                />
              </Grid>
              <Grid container item xs={12} sm={12} md={3} spacing={1} justifyContent={{ lg: "flex-end" }}>
                <Grid item xs={12} sm={8}>
                  <ManageCandidatesButton />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Box>
                <ManageCandidatesTable
                  key={selectedClient.clientId}
                  pagination={{
                    page: page,
                    pageSize: pageSize,
                    search: search,
                    sortBy: orderBy,
                    sortDirection: order,
                  }}
                  handleChangePage={handleChangePage}
                  handleChangePageSize={handleChangeRowsPerPage}
                  handleRequestSort={handleRequestSort}
                  candidatesLoading={isCandidatesLoading}
                  candidates={candidates}
                  rules={rules}
                  search={search}
                />
              </Box>
            </Grid>
          </Grid>
        </Paper>
        <CSVImportModal />
        <MessageDialogCandidateEmail />
        <MessageDialogCandidateText />
        <AddCandidateModal />
        <AddToGroupOptions />
      </CandidateSelectionProvider>
    </PageContainer>
  );
}

export default Candidates;

clearOldLocalStorage("manage-candidates");
